import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hexagon, ArrowRight, Sparkles, Activity, ListChecks, Users, AlertTriangle, DollarSign, Clock, X, Lock, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { api, warmBackend } from "../lib/api";
import { Pill } from "./ui-prim";
import { statusClass } from "../lib/utils";
import { HEALTH_COLORS } from "../lib/accents";
import useSEO from "../lib/useSEO";
import { TrustStrip, SocialProofStrip } from "./marketing/ConversionKit";
import MarketingShowcase from "./marketing/MarketingShowcase";
import { CSuitePreviewPopover } from "./CSuitePreviewPopover";
import { bookingUrl, isExternalBooking } from "../lib/bookingUrl";
import { trackEvent, captureUtmFromUrl } from "../lib/analytics";

// Short pitch per SAP delivery domain — surfaces when a visitor taps a chip
// on the cover page. All 10 domains are subscription-gated, so we always
// close the popup with a "subscribe to unlock" CTA.
const DOMAIN_INFO = {
  "Governance": {
    tagline: "Program & project governance core",
    body: "Steering committees, RACI matrices, decision logs, escalation ladders and audit trails — everything needed to keep a multi-year SAP delivery on the rails and boardroom-ready.",
    tier: "Starter",
  },
  "Fit-to-Standard": {
    tagline: "SAP Best Practices adoption",
    body: "Workshop-driven gap analysis, process fit scoring, deviation register and standard-vs-custom trade-off tracking. Purpose-built for greenfield S/4HANA journeys.",
    tier: "Starter",
  },
  "Data Migration": {
    tagline: "Object catalogue · mock loads · reconciliation",
    body: "Data-object inventory, cleansing scorecards, mock-load cycle tracking, cutover reconciliation reports and go/no-go criteria — mapped to SAP LTMC / Migration Cockpit.",
    tier: "Platinum",
  },
  "Integration": {
    tagline: "Interface register · middleware topology",
    body: "Every inbound / outbound integration mapped end-to-end with owner, protocol (IDoc, OData, SOAP, REST, event), middleware (CPI, PI/PO, Boomi), test status and monitoring hooks.",
    tier: "Platinum",
  },
  "Testing": {
    tagline: "Unit · integration · UAT · regression",
    body: "Test-plan catalog across SIT / UAT / performance / regression waves, defect triage, entry / exit criteria and evidence pack generation for auditors.",
    tier: "Starter",
  },
  "OCM & Training": {
    tagline: "Change impact · adoption · enablement",
    body: "Change-impact heatmap by role, stakeholder journey mapping, training curriculum builder, adoption metrics and hypercare readiness.",
    tier: "Platinum",
  },
  "Cutover & Hypercare": {
    tagline: "Cutover runbook · dress rehearsals · hypercare",
    body: "Minute-by-minute cutover runbook, dress-rehearsal outcomes, dependency waterfall, hypercare command centre and post-go-live stabilisation tracking.",
    tier: "Platinum",
  },
  "Value Realization": {
    tagline: "Planned vs achieved benefits",
    body: "Business-case tracker: planned savings, run-rate benefits, benefit owners, quarterly review cadence and board-ready value dashboards.",
    tier: "Platinum",
  },
  "Reusable IP": {
    tagline: "Templates · accelerators · lessons",
    body: "Library of templates (charters, RAIDs, cutover packs), accelerator scripts, and cross-project lessons learned — searchable and clonable into new projects.",
    tier: "Starter",
  },
  "AI Layer": {
    tagline: "Claude 4.5 across every artefact",
    body: "Natural-language queries over your portfolio, AI-drafted status reports, RAID summarisation, board-update generation and NAVBOT — the executive AI assistant that activates the moment your C-Suite subscription goes live.",
    tier: "Platinum",
  },
};

/**
 * Cover / splash screen — first screen the user sees.
 * Title: "PROJECT HARMONIZER" with byline "BY AJ".
 * Click anywhere or press Enter / Space to proceed to /projects.
 */
export default function CoverPage() {
  const nav = useNavigate();
  const [enter, setEnter] = useState(false);
  const [recent, setRecent] = useState([]);
  const [openDomain, setOpenDomain] = useState(null);
  useSEO({
    title: "Capittance Harmonizer · The SAP Delivery Operating System for CIOs & CFOs",
    description: "Boardroom-grade SAP, Oracle & Dynamics 365 delivery in one live workspace. NAVBOT executive AI (Claude 4.5), Board Pack generator, live D365 + S/4HANA + Oracle Cloud ERP cockpits, private per-tenant workspace. From $200/mo.",
    path: "/",
  });

  // Fetch top 3 recent projects for returning users
  useEffect(() => {
    let alive = true;
    // First-touch analytics — capture UTM from URL and fire homepage_view once.
    captureUtmFromUrl();
    trackEvent("homepage_view");
    // Warm the backend pod immediately so it's awake by the time the
    // user clicks "Enter the Platform".
    warmBackend();
    // Only fetch recent projects when the visitor is signed in — otherwise
    // we spam three 401s into the console on every anonymous landing view.
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("pmv5_csuite_token");
    if (hasToken) {
      api.list().then(list => { if (alive) setRecent((list || []).slice(0, 3)); }).catch(() => {});
    }
    return () => { alive = false; };
  }, []);

  const go = (path) => {
    if (enter) return;
    setEnter(true);
    setTimeout(() => nav(path || "/signup"), 380);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " " || e.code === "Space") {
        e.preventDefault();
        go();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enter]);

  // Decorative orbit nodes — represent the 8 surfaces of the platform.
  const orbits = [
    { icon: Activity,       label: "Dashboard",    a: 0   },
    { icon: ListChecks,     label: "Requirements", a: 45  },
    { icon: AlertTriangle,  label: "RAID",         a: 90  },
    { icon: DollarSign,     label: "Budget",       a: 135 },
    { icon: Sparkles,       label: "AI",           a: 180 },
    { icon: Users,          label: "Stakeholders", a: 225 },
    { icon: Activity,       label: "Tracker",      a: 270 },
    { icon: Hexagon,        label: "Gap/Fit",      a: 315 },
  ];

  return (
    <div
      className={`min-h-screen w-full relative overflow-hidden grid-bg select-none ${enter ? "cover-exit" : ""}`}
      onClick={go}
      data-testid="cover-page"
    >
      {/* Background decorative gradients — Ocean Blue + White */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-32 -left-32 w-[640px] h-[640px] rounded-full blur-3xl opacity-60"
             style={{ background: "radial-gradient(closest-side, rgba(3,105,161,0.30), transparent)" }} />
        <div className="absolute top-1/4 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-55"
             style={{ background: "radial-gradient(closest-side, rgba(14,165,233,0.28), transparent)" }} />
        <div className="absolute -bottom-40 left-1/3 w-[600px] h-[600px] rounded-full blur-3xl opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(6,182,212,0.26), transparent)" }} />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(56,189,248,0.22), transparent)" }} />
      </div>

      {/* Header strip */}
      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/" className="inline-flex items-center gap-3" aria-label="Capittance home">
            <Logo height={90} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hidden md:inline">v5 · 2026</span>
          </a>
        </div>
        <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          <nav className="hidden md:flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <a href="/about"   className="hover:text-zinc-900" data-testid="nav-about">About</a>
            <a href="/pricing" className="hover:text-zinc-900" data-testid="nav-pricing">Pricing</a>
            <a href="/enterprise" className="hover:text-zinc-900" data-testid="nav-enterprise">Enterprise</a>
            <a href="/vs/microsoft-project" className="hover:text-zinc-900" data-testid="nav-vs">vs MS Project</a>
            <a href="/resources" className="hover:text-zinc-900" data-testid="nav-resources">Resources</a>
            <a href="/guides" className="hover:text-zinc-900" data-testid="nav-guides">Guides</a>
            <a href="/tools" className="hover:text-zinc-900" data-testid="nav-tools">Free tools</a>
            <a href="/contact" className="hover:text-zinc-900" data-testid="nav-contact">Contact</a>
          </nav>
          <span className="hidden lg:inline text-zinc-400">·</span>
          <span onClick={(e) => e.stopPropagation()}>
            <ThemeToggle />
          </span>
        </div>
      </header>

      {/* Center hero */}
      <main className="relative z-10 px-6 lg:px-12 py-10 flex flex-col items-center justify-center min-h-[calc(100vh-200px)]">
        <div className="w-full max-w-6xl mx-auto">

          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-3 mb-10 anim-in" style={{ animationDelay: "60ms" }}>
            <span className="h-px w-12 bg-zinc-300" />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-zinc-500">Intelligent Project & Portfolio Management Portal</span>
            <span className="h-px w-12 bg-zinc-300" />
          </div>

          {/* Title — brand wordmark */}
          <h1 className="font-display text-center tracking-tighter leading-[0.88] text-zinc-900 anim-in" style={{ animationDelay: "120ms" }}>
            <div className="text-[clamp(72px,15vw,220px)] font-extrabold relative inline-block bg-clip-text text-transparent"
                 style={{ backgroundImage: "linear-gradient(120deg, var(--navy-deep) 0%, var(--navy) 35%, var(--burgundy) 75%, var(--burgundy-deep) 100%)" }}>
              HARMONIZER
              <span className="absolute -right-2 -top-2 md:-right-4 md:-top-3 w-2 h-2 md:w-3 md:h-3 rounded-full animate-pulse"
                    style={{ background: "var(--burgundy)" }} />
            </div>
          </h1>

          {/* Master positioning headline */}
          <h2 className="text-center font-display text-2xl md:text-4xl font-extrabold tracking-tight text-zinc-900 mt-8 max-w-4xl mx-auto leading-[1.1] anim-in" style={{ animationDelay: "280ms" }} data-testid="cover-master-headline">
            Run every project. <span className="text-[#7A1F2B]">See every risk.</span> Give leadership one reliable view.
          </h2>

          {/* Supporting statement */}
          <p className="text-center text-zinc-600 mt-6 max-w-3xl mx-auto leading-relaxed text-sm md:text-base anim-in" style={{ animationDelay: "340ms" }} data-testid="cover-master-subline">
            Manage plans, milestones, budgets, RAID, testing, documents, stakeholders, cutover and value realization —
            <span className="text-zinc-900 font-medium"> while giving executives live portfolio visibility and board-ready reporting</span>.
          </p>

          {/* Three-CTA row — one door per audience */}
          <div className="flex items-center justify-center gap-3 mt-8 flex-wrap anim-in" style={{ animationDelay: "400ms" }} data-testid="cover-three-cta-row">
            <a
              href="/csuite/demo"
              className="btn btn-primary shadow-lg inline-flex items-center gap-1.5"
              onClick={() => trackEvent("executive_portal_selected", { placement: "hero_primary" })}
              data-testid="cover-cta-explore-executive"
            >
              Explore the Executive Portal &nbsp;→
            </a>
            <a
              href={bookingUrl("walkthrough")}
              {...(isExternalBooking() ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="btn btn-secondary inline-flex items-center gap-1.5"
              onClick={(e) => { e.stopPropagation(); trackEvent("walkthrough_clicked", { placement: "hero_secondary" }); }}
              data-testid="cover-cta-book-walkthrough"
            >
              Book a 20-Minute Walkthrough
            </a>
          </div>
          <div className="text-center mt-2 anim-in" style={{ animationDelay: "440ms" }} data-testid="cover-explore-hint">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              No credit card · no sales call · fictional demo data
            </span>
          </div>
          <div className="text-center mt-3 anim-in" style={{ animationDelay: "460ms" }}>
            <a
              href="/csuite/signup?plan=pro"
              className="text-xs font-mono text-[#7A1F2B] hover:text-[#4A0F1A] underline underline-offset-2"
              onClick={() => trackEvent("executive_trial_started", { placement: "hero_link" })}
              data-testid="cover-cta-start-trial"
            >
              Ready to use your own data? Start Executive Trial →
            </a>
          </div>
          <div className="text-center mt-3 anim-in" style={{ animationDelay: "480ms" }}>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">Agile · Waterfall · Hybrid · PRINCE2 · SAFe · SAP Activate  ·  Governance, delivery, testing, cutover, hypercare, value realization</span>
          </div>
          <div className="text-center mt-2 anim-in" style={{ animationDelay: "500ms" }}>
            <button onClick={(e) => { e.stopPropagation(); nav("/login"); }} className="text-[11px] font-mono text-zinc-400 hover:text-zinc-700 underline underline-offset-2" data-testid="cover-signin">
              Already have an account? Sign in →
            </button>
          </div>

          {/* Orbit chips — 10 domains. Each opens a short explainer modal. */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2 anim-in" style={{ animationDelay: "420ms" }}>
            {["Governance","Fit-to-Standard","Data Migration","Integration","Testing","OCM & Training","Cutover & Hypercare","Value Realization","Reusable IP","AI Layer"].map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenDomain(s); }}
                className="pill pill-neutral hover:border-[var(--navy-soft)] hover:text-[var(--navy-deep)] cursor-pointer transition-colors"
                data-testid={`cover-chip-${i}`}
                title={`Learn about ${s}`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Audience selector — three doors, one per buyer */}
          <section id="audience-selector" className="mt-16 anim-in" style={{ animationDelay: "520ms" }} data-testid="cover-audience-selector">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="h-px w-12 bg-zinc-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">What do you need Capittance to do?</span>
              <span className="h-px w-12 bg-zinc-300" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
              <AudienceCard
                testid="audience-card-team"
                eyebrow="Project teams"
                title="Manage a project"
                body="Everything your project team needs — from planning through value realization. Plans, milestones, RAID, actions, budgets, requirements, testing, stakeholders, documents, exports."
                ctaLabel="Start a 14-Day Team Trial"
                ctaTestId="audience-cta-team"
                onCta={(e) => { e.stopPropagation(); trackEvent("team_portal_selected", { placement: "audience_card" }); go(); }}
                accent="#0369A1"
              />
              <AudienceCard
                testid="audience-card-csuite"
                eyebrow="Executives"
                title="Govern a portfolio"
                body="One executive view across every strategic project. Portfolio health, budget exposure, strategic milestones, cross-project dependencies, benefits realization, board packs."
                ctaLabel="Explore Executive Portal"
                ctaTestId="audience-cta-csuite"
                ctaHref="/csuite/demo"
                onCta={() => trackEvent("executive_portal_selected", { placement: "audience_card" })}
                accent="#7A1F2B"
                featured
              />
              <AudienceCard
                testid="audience-card-enterprise"
                eyebrow="Transformation programs"
                title="Control a transformation"
                body="Control complex SAP, D365 or Oracle transformations without replacing every delivery tool. Fit-to-standard, data migration, integration, testing, OCM, cutover, hypercare, value."
                ctaLabel="Request Program Assessment"
                ctaTestId="audience-cta-enterprise"
                ctaHref="/enterprise"
                onCta={() => trackEvent("enterprise_edition_selected", { placement: "audience_card" })}
                accent="#C9A66B"
              />
            </div>
            <div className="text-center mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              Team $200/mo · Executive $350/mo · Enterprise Transformation — custom
            </div>
          </section>

          {/* Trust + social proof — conversion signals directly under primary CTA */}
          <div className="mt-10 anim-in" onClick={(e) => e.stopPropagation()} style={{ animationDelay: "680ms" }}>
            <TrustStrip />
            <SocialProofStrip />
          </div>

          {/* Full marketing showcase — live cockpit + bento grid + trust ribbon */}
          <div onClick={(e) => e.stopPropagation()}>
            <MarketingShowcase />
          </div>

          {/* Multi-tenant + NAVBOT activation — the new operating model */}
          <section
            onClick={(e) => e.stopPropagation()}
            className="mt-14 anim-in"
            style={{ animationDelay: "780ms" }}
            data-testid="section-tenancy-navbot"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#C9A66B]/40" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#7A1F2B]">
                Your workspace  ·  your data  ·  your AI
              </span>
              <span className="h-px w-12 bg-[#C9A66B]/40" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card 1 — Private tenant workspace */}
              <div className="p-6 rounded-md border" data-testid="card-tenant-isolation"
                   style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #FDF2F4 100%)", borderColor: "#F0D3D8" }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-9 h-9 rounded-sm grid place-items-center text-white"
                        style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}>
                    <ShieldCheck size={16} strokeWidth={2.2}/>
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A1F2B]">
                      Multi-tenant · Isolated
                    </div>
                    <div className="font-display text-lg font-extrabold text-[var(--navy)]">Your team gets its own private workspace</div>
                  </div>
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  On signup, we automatically provision an <b>Organization</b> for your company. Every project, RAID log, budget, dashboard, and Board Pack belongs to <em>your</em> tenant only — invisible to other companies on the platform. No shared workspaces, no cross-org leakage, no manual configuration.
                </p>
                <ul className="mt-3 space-y-1.5 text-[13px] text-zinc-700">
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Automatic org creation from your company name at signup</li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Owner + Member roles — invite teammates to <em>your</em> org only</li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Every project query filtered by <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-[#FDF2F4]">org_id</code> at the database layer</li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Full audit trail per organization</li>
                </ul>
              </div>

              {/* Card 2 — NAVBOT activation */}
              <div className="p-6 rounded-md border" data-testid="card-navbot-activation"
                   style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #F7ECEE 100%)", borderColor: "#F0D3D8" }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-9 h-9 rounded-sm grid place-items-center text-white"
                        style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
                    <Sparkles size={16} strokeWidth={2.2}/>
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A1F2B]">
                      NAVBOT  ·  C-Suite only  ·  Claude 4.5
                    </div>
                    <div className="font-display text-lg font-extrabold text-[var(--navy)]">Your executive AI activates the second you pay</div>
                  </div>
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  <b>NAVBOT</b> is your executive AI assistant — Claude Sonnet 4.5 wired to <em>your</em> live portfolio data. It starts responding the moment your C-Suite subscription goes active. No setup, no training, no config: sign up, complete Stripe checkout, and NAVBOT is live in your portal within seconds.
                </p>
                <ul className="mt-3 space-y-1.5 text-[13px] text-zinc-700">
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Chat mode on Starter · proactive quick-actions on Platinum</li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Grounded on your tenant's data only — no cross-org bleed</li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Draft board updates, summarise RAID logs, answer portfolio questions</li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-[#7A1F2B]">▸</span> Zero-retention posture with Anthropic — your prompts never train the model</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Recent projects — only shown for returning users */}
          {recent.length > 0 && (
            <div className="mt-14 anim-in" onClick={(e) => e.stopPropagation()} style={{ animationDelay: "720ms" }}>
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className="h-px w-12 bg-zinc-300" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 flex items-center gap-1.5">
                  <Clock size={11} strokeWidth={2}/> Continue your work
                </span>
                <span className="h-px w-12 bg-zinc-300" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                {recent.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => go(`/projects/${p.id}`)}
                    data-testid={`recent-project-${p.id}`}
                    style={{ "--health": HEALTH_COLORS[p.health] || "var(--navy)" }}
                    className="card health-strip text-left p-4 pl-5 hover:border-zinc-400 hover:-translate-y-0.5 transition-all group cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.14em] truncate">{p.code}</div>
                        <div className="font-display text-base font-bold text-zinc-900 truncate mt-0.5">{p.name}</div>
                      </div>
                      <Pill tone={statusClass(p.health).replace("pill-", "")}>{p.health}</Pill>
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 flex items-center justify-between">
                      <span className="truncate">{p.counts?.tasks || 0} tasks · {p.counts?.requirements || 0} reqs</span>
                      <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-center mt-4">
                <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); go("/projects"); }} data-testid="cover-see-all">
                  See all projects →
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer strip */}
      <footer className="relative z-10 px-6 lg:px-12 py-5 border-t border-[var(--border)] flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">
          © 2026 Capittance · All projects, one harmony
        </div>
        <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/> System online</span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">Edition v5.0</span>
          <span className="hidden sm:inline">·</span>
          <a href="/guides" className="underline hover:no-underline hover:text-zinc-900" data-testid="cover-footer-guides">Guides</a>
          <a href="/compare" className="underline hover:no-underline hover:text-zinc-900" data-testid="cover-footer-compare">Compare</a>
          <a href="/tools" className="underline hover:no-underline hover:text-zinc-900" data-testid="cover-footer-tools">Free tools</a>
          <span className="hidden sm:inline">·</span>
          <a href="/testing-admin"
             className="underline hover:no-underline hover:text-zinc-900"
             data-testid="cover-testing-admin-link">
            Testing_Admin
          </a>
          <span className="hidden sm:inline">·</span>
          <a href="mailto:Requests@Capittance.com?subject=Capittance%20support%20request"
             className="underline hover:no-underline"
             data-testid="cover-support-link">
            Requests@Capittance.com
          </a>
        </div>
      </footer>

      {openDomain && (
        <DomainModal domain={openDomain} info={DOMAIN_INFO[openDomain]} onClose={() => setOpenDomain(null)} nav={nav} />
      )}

      <style>{`
        .cover-exit { animation: coverOut 0.38s cubic-bezier(0.7, 0, 0.84, 0) both; }
        @keyframes coverOut {
          to { opacity: 0; transform: scale(1.04); filter: blur(6px); }
        }
      `}</style>
    </div>
  );
}

// Domain explainer modal — subscription-gated pitch for one of the 10 SAP
// delivery domains. Always includes a "Subscribe to unlock" CTA that jumps
// the visitor into the pricing page (Platinum pre-selected when relevant).
function DomainModal({ domain, info, onClose, nav }) {
  const isPro = info?.tier === "Platinum";
  const goSubscribe = () => {
    onClose();
    nav(`/signup?plan=${isPro ? "pro" : "starter"}`);
  };
  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center px-4"
      style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="domain-modal-backdrop"
    >
      <div
        className="bg-white rounded-sm shadow-2xl max-w-lg w-full p-6 md:p-8 relative"
        style={{ borderTop: "4px solid var(--navy)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="domain-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-800"
          data-testid="domain-modal-close"
          aria-label="Close"
        >
          <X size={18}/>
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">
          SAP Delivery Domain · {isPro ? "Platinum tier" : "Starter tier"}
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">
          {domain}
        </h2>
        <div className="text-sm font-medium text-[var(--navy)] mt-1">{info?.tagline}</div>
        <p className="text-sm text-zinc-700 leading-relaxed mt-4">{info?.body}</p>

        <div className="mt-5 flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 border border-[var(--border)] rounded-sm p-3">
          <Lock size={14} className="mt-0.5 shrink-0 text-zinc-400"/>
          <span>
            This module is <b>not functional</b> until you subscribe.{" "}
            {isPro
              ? "Available on Platinum ($350/mo)."
              : "Available on Starter ($200/mo) and Platinum ($350/mo)."}
          </span>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="domain-modal-cancel">
            Close
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => { onClose(); nav("/pricing"); }} data-testid="domain-modal-pricing">
            See pricing
          </button>
          <button className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                  onClick={goSubscribe}
                  data-testid="domain-modal-subscribe">
            Subscribe to unlock <ArrowRight size={13}/>
          </button>
        </div>
      </div>
    </div>
  );
}

// Audience selector card — one per buyer journey. Renders a coloured accent
// bar, eyebrow, headline, description and CTA. Distinguishes internal-route
// vs external-Calendly CTAs via `ctaExternal`.
function AudienceCard({ testid, eyebrow, title, body, ctaLabel, ctaTestId, ctaHref, ctaExternal, onCta, accent = "#0369A1", featured }) {
  const CtaWrap = ctaHref ? "a" : "button";
  const ctaProps = ctaHref
    ? { href: ctaHref, ...(ctaExternal ? { target: "_blank", rel: "noopener noreferrer" } : {}) }
    : { type: "button" };
  return (
    <div
      className={`relative bg-white rounded-md border p-5 flex flex-col shadow-sm hover:shadow-md transition-shadow ${featured ? "ring-2 ring-offset-2" : ""}`}
      style={featured ? { borderColor: accent + "44", "--tw-ring-color": accent + "55" } : { borderColor: "#E5E7EB" }}
      data-testid={testid}
    >
      <div className="h-1 rounded-full -mt-5 -mx-5 mb-4" style={{ background: accent }} />
      <div className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: accent }}>{eyebrow}</div>
      <div className="font-display text-xl font-extrabold tracking-tight text-zinc-900 mt-1">{title}</div>
      <p className="text-sm text-zinc-600 leading-relaxed mt-2 flex-1">{body}</p>
      <CtaWrap
        {...ctaProps}
        onClick={onCta}
        className="mt-4 inline-flex items-center justify-between gap-2 rounded-sm px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition-colors"
        style={{ background: accent }}
        data-testid={ctaTestId}
      >
        <span>{ctaLabel}</span>
        <ArrowRight size={14} strokeWidth={2.4}/>
      </CtaWrap>
    </div>
  );
}

